import axios from "../utils/axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

// Helper function to convert tradingHours object to array format expected by backend
const convertTradingHoursToArray = (tradingHours) => {
  if (!tradingHours) return undefined;

  const daysMap = {
    monday: "monday",
    tuesday: "tuesday",
    wednesday: "wednesday",
    thursday: "thursday",
    friday: "friday",
    saturday: "saturday",
    sunday: "sunday"
  };

  // If already an array, normalize the day casing to lowercase and ensure openTime/closeTime
  if (Array.isArray(tradingHours)) {
    return tradingHours
      .filter(item => item && item.day && (item.openTime || item.closeTime || item.open || item.close))
      .map((item) => {
        const d = String(item.day).toLowerCase();
        return {
          day: daysMap[d] || String(item.day).toLowerCase(),
          openTime: item.openTime || item.open || "",
          closeTime: item.closeTime || item.close || ""
        };
      });
  }

  // Convert object format to array format
  return Object.entries(tradingHours)
    .filter(([_, hours]) => hours?.open || hours?.close || hours?.openTime || hours?.closeTime)
    .map(([day, hours]) => ({
      day: daysMap[day.toLowerCase()] || day.toLowerCase(),
      openTime: hours?.open || hours?.openTime || "",
      closeTime: hours?.close || hours?.closeTime || ""
    }));
};

export const addFranchise = createAsyncThunk(
  "location",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const merchantId = selectedMerchant?._id || payload?.merchant;
      const url = merchantId ? `location?merchantId=${merchantId}` : "location";

      // If `locations` key exists and is a non-empty array, send only that
      const getPlaceId = (val) => {
        if (!val) return "";
        if (typeof val === 'object') {
          return val?.googlePlaceId || val?.placeId || val?.place_id || val?.id || "";
        }
        // If it looks like a full address (contains spaces/commas) and we have no other option, 
        // return empty or the val. But purely for GooglePlaceId field, it should be the ID.
        return val;
      };

      const dataToSend = Array.isArray(payload.locations)
        ? {
          merchant: merchantId,
          locations: payload.locations.map((loc) => ({
            name: loc?.name,
            email: loc?.email || "",
            phone: loc?.phone || "",
            googlePlaceId: getPlaceId(loc?.googlePlaceId),
            tradingHours: convertTradingHoursToArray(loc?.workingHours || loc?.tradingHours),
          })),
        }
        : {
          merchant: payload?.merchant,
          name: payload.name,
          email: payload.email,
          phone: payload.phone || "",
          googlePlaceId: getPlaceId(payload?.googlePlaceId),
          tradingHours: convertTradingHoursToArray(payload.tradingHours),
        };

      const response = await axios.post(url, dataToSend);
      return response?.data;
    } catch (error) {
      const raw = error?.response?.data || error?.message;
      const message = typeof raw === "string" ? raw : raw?.message;

      // Check for different types of duplicates
      const isDupPhone = (typeof message === "string" && /E11000/i.test(message) && /phone/i.test(message)) ||
        (typeof message === "string" && /This number already exist/i.test(message));

      const isDupEmail = typeof message === "string" && /E11000/i.test(message) && /email/i.test(message);

      const isDupName = typeof message === "string" && /E11000/i.test(message) && /name/i.test(message);

      let finalMessage;
      if (isDupPhone) {
        finalMessage = "A store location with this phone number is already registered. Please use a different number.";
      } else if (isDupEmail) {
        finalMessage = "A store location with this email address is already registered. Please use a different email.";
      } else if (isDupName) {
        finalMessage = "A store location with this name already exists. Please use a different name.";
      } else {
        finalMessage = message || "Failed to create location";
      }

      return thunkAPI.rejectWithValue({
        success: false,
        message: finalMessage,
      });
    }
  }
);

export const getFranchises = createAsyncThunk(
  "allLocations",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const query = new URLSearchParams();

      if (payload?.merchant) query.append("merchant", payload.merchant);
      if (payload?.page) query.append("page", payload.page);
      if (payload?.search) query.append("search", payload.search);
      if (payload?.duration) query.append("duration", payload.duration);

      if (payload?.limit) query.append("limit", payload.limit);
      else if (!payload?.duration) query.append("limit", 100);

      if (selectedMerchant?._id) query.append("merchantId", selectedMerchant._id);

      const url = `location/merchant?${query.toString()}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const toggleFranchiseStatus = createAsyncThunk(
  "location/status",
  async (payload, thunkAPI) => {
    try {
      const response = await axios.patch(`location/status/${payload?.id}`);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const editFranchise = createAsyncThunk(
  "location/update",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const merchantId = selectedMerchant?._id || payload?.merchant;
      const url = merchantId
        ? `location/${payload?.id}?merchantId=${merchantId}`
        : `location/${payload?.id}`;

      const getPlaceId = (val) => {
        if (!val) return "";
        if (typeof val === 'object') {
          return val?.googlePlaceId || val?.placeId || val?.place_id || val?.id || "";
        }
        return val;
      };

      // Ensure tradingHours format is correct for the backend (uppercase days, etc)
      const dataToSend = { ...payload };
      if (dataToSend.tradingHours) {
        dataToSend.tradingHours = convertTradingHoursToArray(dataToSend.tradingHours);
      }
      if (dataToSend.googlePlaceId) {
        dataToSend.googlePlaceId = getPlaceId(dataToSend.googlePlaceId);
      }

      const response = await axios.patch(url, dataToSend);
      return response?.data;
    } catch (error) {
      const raw = error?.response?.data || error?.message;
      const message = typeof raw === "string" ? raw : raw?.message;

      // Check for different types of duplicates
      const isDupPhone = (typeof message === "string" && /E11000/i.test(message) && /phone/i.test(message)) ||
        (typeof message === "string" && /This number already exist/i.test(message));

      const isDupEmail = typeof message === "string" && /E11000/i.test(message) && /email/i.test(message);

      const isDupName = typeof message === "string" && /E11000/i.test(message) && /name/i.test(message);

      let finalMessage;
      if (isDupPhone) {
        finalMessage = "A store location with this phone number is already registered. Please use a different number.";
      } else if (isDupEmail) {
        finalMessage = "A store location with this email address is already registered. Please use a different email.";
      } else if (isDupName) {
        finalMessage = "A store location with this name already exists. Please use a different name.";
      } else {
        finalMessage = message || "Failed to update location";
      }

      return thunkAPI.rejectWithValue({
        success: false,
        message: finalMessage,
      });
    }
  }
);

export const deleteFranchise = createAsyncThunk(
  "location/delete",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    try {
      const merchantId = selectedMerchant?._id || payload?.merchant;
      const url = merchantId
        ? `location/${payload?.id}?merchantId=${merchantId}`
        : `location/${payload?.id}`;
      const response = await axios.delete(url);
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);

export const getLocationById = createAsyncThunk(
  "location/id",
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const selectedMerchant = state.merchantReducer.selectedMerchent;
    const params = selectedMerchant ? { merchantId: selectedMerchant._id } : {};
    try {
      const response = await axios.get(`location/${payload?.id}`, { params });
      return response?.data;
    } catch (error) {
      return thunkAPI?.rejectWithValue(error);
    }
  }
);